/*************************************************************************
 * ADOBE CONFIDENTIAL
 * ___________________
 *
 *  Copyright 2022 Adobe
 *  All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Adobe and its suppliers, if any. The intellectual
 * and technical concepts contained herein are proprietary to Adobe
 * and its suppliers and are protected by all applicable intellectual
 * property laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe.
 **************************************************************************/
import { getLMServiceWorkerScopePrefix, sizeOfLiquidModeResource, getBroadcastChannel } from "utils/LiquidModeUtils";
import addEventListener from "dc-preview-utils/src/Utils/DomUtils.js";
import { registerServiceWorker, addServiceWorkerEventListener } from "dc-preview-utils/src/Utils/ServiceWorkerUtils";
import { liquidModeNetworkProxyTechnology } from "constants/enums.js";
import { recordAJSResourceResponseTime, recordAJSResourceRequestTime } from "utils/performance";
import { trackLiquidModeAJSResourcesFetched } from "analytics/events";
import { envUtil } from "dc-core";
import { reportURIEndPoints } from "constants/enums";

class ServiceWorkerHandler {
  static _instance;

  constructor({ fetchDataCallback, serviceWorkerScope }) {
    this._swScope = serviceWorkerScope;
    this._swRegistration = null;

    if (fetchDataCallback) {
      this._fetchDataCallback = fetchDataCallback;
    }

    this._onServiceWorkerRegistered = this._onServiceWorkerRegistered.bind(this);
    this._onServiceWorkerActivated = this._onServiceWorkerActivated.bind(this);
    this._onServiceWorkerStateChange = this._onServiceWorkerStateChange.bind(this);
    this._onMessageFromServiceWorker = this._onMessageFromServiceWorker.bind(this);
    this._unregisterServiceWorker = this._unregisterServiceWorker.bind(this);
  }

  static getInstance(props) {
    if (!ServiceWorkerHandler._instance) {
      ServiceWorkerHandler._instance = new ServiceWorkerHandler(props);
    }
    return ServiceWorkerHandler._instance;
  }

  static reset() {
    if (ServiceWorkerHandler._instance) {
      ServiceWorkerHandler._instance._unregisterServiceWorker();
      ServiceWorkerHandler._instance = undefined;
    }
  }

  get _swHandle() {
    return this._swRegistration.active || this._swRegistration.waiting || this._swRegistration.installing;
  }

  setUpServiceWorker() {
    if (this._swRegistration && this._swRegistration.active) {
      return Promise.resolve({
        status: true,
        scope: this._swScope,
        networkProxyTechnology: liquidModeNetworkProxyTechnology.SERVICE_WORKERS
      });
    }
    return new Promise((resolve, reject) => {
      this._onServiceWorkerSetupDone = statusObj => {
        const { status } = statusObj;
        if (status) {
          resolve(statusObj);
        } else {
          reject(statusObj);
        }
      };
      this._registerServiceWorker();
    });
  }

  _registerServiceWorker() {
    require("./serviceworker.sw.js");

    registerServiceWorker(`${getLMServiceWorkerScopePrefix()}/serviceworker.sw.js`, this._swScope)
      .then(registration => {
        this._onServiceWorkerRegistered(registration);
      })
      .catch(err => {
        this._onServiceWorkerSetupDone({
          status: false,
          errorMsg: `Service Worker Registration Error:${err}`
        });
      });
  }

  _onServiceWorkerRegistered(registration) {
    this._swRegistration = registration;

    if (registration.active) {
      this._onServiceWorkerActivated();
    }

    if (this._swHandle) {
      this._swHandle.onstatechange = this._onServiceWorkerStateChange;
    }
  }

  _onServiceWorkerActivated() {
    addEventListener(window, "beforeunload", () => {
      ServiceWorkerHandler.reset();
    });
    addServiceWorkerEventListener("onmessage", this._onMessageFromServiceWorker);

    this._registerCommunicationChannel();
    this._setReportURI();
    this._onServiceWorkerSetupDone({
      status: true,
      scope: this._swScope,
      networkProxyTechnology: liquidModeNetworkProxyTechnology.SERVICE_WORKERS
    });
  }

  _registerCommunicationChannel() {
    this.communicationChannel = getBroadcastChannel(this._swScope);
    this.communicationChannel.onmessage = event => {
      this._onMessageFromServiceWorker(event);
    };
  }

  _onServiceWorkerStateChange(evt) {
    if (evt.target) {
      switch (evt.target.state) {
        case "activated":
          this._onServiceWorkerActivated();
          return;
        default:
      }
    }
  }

  _onMessageFromServiceWorker(evt) {
    if (evt && evt.data) {
      const { error, scope, resourceID, resourceURL } = evt.data;
      if (!error && this._swScope === scope && this._fetchDataCallback) {
        recordAJSResourceRequestTime(resourceID);
        this._fetchDataCallback(resourceID, resourceURL).then(resp => {
          // this._swHandle.postMessage(resp);
          this.communicationChannel.postMessage(resp);
          recordAJSResourceResponseTime(
            resourceID,
            sizeOfLiquidModeResource(resp.data.resourceContent),
            resp.data.resourceMimeType
          );
          trackLiquidModeAJSResourcesFetched();
        });
      }
    }
  }

  _unregisterServiceWorker() {
    this._swRegistration.unregister();
  }

  _setReportURI() {
    const hostEnv = envUtil.getHostEnv();
    let reportURI;

    switch (hostEnv) {
      case "prod":
        reportURI = reportURIEndPoints.PROD_ENDPOINT;
        break;
      case "stage":
        reportURI = reportURIEndPoints.STAGE_ENDPOINT;
        break;
      default:
        reportURI = reportURIEndPoints.DEV_ENDPOINT;
    }

    const setReportURIMsg = { command: "setReportURI", reportURI };
    this.communicationChannel.postMessage(setReportURIMsg);
  }
}

export default ServiceWorkerHandler;
